package com.milta.core.network

import com.milta.feature.auth.data.remote.dto.ApiError

/**
 * MIL-98 — 403 `error.ai_credit_exhausted`: the caller's AI credit is at 0, the call was refused
 * before reaching Claude (scan, PDF import, unrecognised spreadsheet columns, import submissions and
 * relaunches). [resetAt] is the ISO instant of the next renewal, as sent by the API.
 */
class AiCreditExhaustedException(val resetAt: String?) : ApiException(HTTP_FORBIDDEN, CODE) {
    companion object {
        const val CODE = "error.ai_credit_exhausted"
        private const val HTTP_FORBIDDEN = 403
    }
}

/** True for the AI credit 403, whatever exception class carried it. */
val Throwable.isAiCreditExhausted: Boolean
    get() = this is ApiException && message == AiCreditExhaustedException.CODE

/** The reset date carried by an AI credit 403, if [this] is one. */
val Throwable.aiCreditResetAt: String?
    get() = (this as? AiCreditExhaustedException)?.resetAt

/**
 * The exception of a non-2xx answer: the MIL-97 / MIL-98 usage-limit 403s keep their extensions,
 * anything else is a plain [ApiException] carrying the backend code.
 */
fun apiExceptionOf(status: Int, apiError: ApiError?): ApiException = when (apiError?.code) {
    AiCreditExhaustedException.CODE -> AiCreditExhaustedException(apiError.resetAt)
    OptimizationQuotaExceededException.CODE ->
        OptimizationQuotaExceededException(apiError.limit, apiError.remaining, apiError.resetAt)
    else -> ApiException(status, apiError?.code ?: "error_$status")
}
